import { Pipe, PipeTransform } from '@angular/core';
import { Observable } from 'rxjs';
import { isNullOrUndefined } from 'util';
import { leftEyeKey, rightEyeKey, symptomMethodKey } from '../../form.model';
import { KeyValueMap } from '../services/schema/schema.model';
import { SchemaService } from '../services/schema/schema.service';
import { Symptom, SymptomMethod, SymptomModes, SymptomTypes } from '../symptom.model';
import { DrawSymptomService } from './../draw-symptom-control/draw-symptom.service';

export interface SymptomRightLeftValues {
  key: string;
  rightEye?: SymptomValue;
  leftEye?: SymptomValue;
  type: SymptomTypes;
}

export interface SymptomValue {
  value: string | number | Observable<string>;
  method?: SymptomMethod;
  units?: string;
  trend?: 'up' | 'down' | null;
  isImprovement?: boolean | null;
  previousValue?: string | number;
}

@Pipe({
  name: 'symptoms'
})
export class SymptomsPipe implements PipeTransform {
  constructor(
    private schemaService: SchemaService,
    private drawSymptomService: DrawSymptomService
  ) {}

  transform(
    formKeyValue: KeyValueMap,
    mode?: SymptomModes,
    previousFormKeyValue?: KeyValueMap
  ): SymptomRightLeftValues[] {
    const result: SymptomRightLeftValues[] = [];
    this.schemaService.symptomKeys.forEach(key => {
      const symptom: Symptom = this.schemaService.symptomMap[key];
      const type = symptom.type;

      const rightEyeFormKey = rightEyeKey(key);
      const leftEyeFormKey = leftEyeKey(key);
      const rightMethodFormKey = symptomMethodKey(rightEyeFormKey);
      const leftMethodFormKey = symptomMethodKey(leftEyeFormKey);
      const rightValue = formKeyValue[rightEyeFormKey];
      const leftValue = formKeyValue[leftEyeFormKey];
      const rightMethod = formKeyValue[rightMethodFormKey] as SymptomMethod;
      const leftMethod = formKeyValue[leftMethodFormKey] as SymptomMethod;
      const rightUnits = this.schemaService.getUnitsForSymptomKey(rightEyeKey(key), rightMethod);
      const leftUnits = this.schemaService.getUnitsForSymptomKey(leftEyeKey(key), leftMethod);

      // Get previous values if available
      let previousRightValue: string | number;
      let previousLeftValue: string | number;
      let previousRightMethod: SymptomMethod;
      let previousLeftMethod: SymptomMethod;

      if (previousFormKeyValue) {
        previousRightValue = previousFormKeyValue[rightEyeFormKey];
        previousLeftValue = previousFormKeyValue[leftEyeFormKey];
        previousRightMethod = previousFormKeyValue[rightMethodFormKey] as SymptomMethod;
        previousLeftMethod = previousFormKeyValue[leftMethodFormKey] as SymptomMethod;
      }

      if (rightValue || leftValue) {
        result.push(
          this.createSymptomRightLeftValues(
            key,
            rightValue,
            leftValue,
            rightMethod,
            leftMethod,
            rightUnits,
            leftUnits,
            type,
            previousRightValue,
            previousLeftValue,
            previousRightMethod,
            previousLeftMethod
          )
        );
      }
    });
    return result;
  }

  private createSymptomRightLeftValues(
    key: string,
    rightEyeValue: string | number,
    leftEyeValue: string | number,
    rightEyeMethod: SymptomMethod,
    leftEyeMethod: SymptomMethod,
    rightEyeUnits: string,
    leftEyeUnits: string,
    type: SymptomTypes,
    previousRightValue?: string | number,
    previousLeftValue?: string | number,
    previousRightMethod?: SymptomMethod,
    previousLeftMethod?: SymptomMethod
  ): SymptomRightLeftValues {
    const value: SymptomRightLeftValues = { key, type };

    if (!isNullOrUndefined(rightEyeValue)) {
      const trendInfo = this.calculateTrend(
        key,
        rightEyeValue,
        previousRightValue,
        rightEyeMethod,
        previousRightMethod
      );

      value.rightEye = {
        value:
          type !== SymptomTypes.Draw
            ? rightEyeValue
            : this.drawSymptomService.getImageUrlFromJSON(rightEyeValue),
        method: rightEyeMethod,
        units: rightEyeUnits,
        trend: trendInfo.trend,
        isImprovement: trendInfo.isImprovement,
        previousValue: previousRightValue
      };
    }
    if (!isNullOrUndefined(leftEyeValue)) {
      const trendInfo = this.calculateTrend(
        key,
        leftEyeValue,
        previousLeftValue,
        leftEyeMethod,
        previousLeftMethod
      );

      value.leftEye = {
        value:
          type !== SymptomTypes.Draw
            ? leftEyeValue
            : this.drawSymptomService.getImageUrlFromJSON(leftEyeValue),
        method: leftEyeMethod,
        units: leftEyeUnits,
        trend: trendInfo.trend,
        isImprovement: trendInfo.isImprovement,
        previousValue: previousLeftValue
      };
    }

    return value;
  }

  private calculateTrend(
    symptomKey: string,
    currentValue: string | number,
    previousValue: string | number,
    currentMethod: SymptomMethod,
    previousMethod: SymptomMethod
  ): { trend: 'up' | 'down' | null; isImprovement: boolean | null } {
    if (currentValue == null || previousValue == null) {
      return { trend: null, isImprovement: null };
    }

    if (currentValue === previousValue) {
      return { trend: null, isImprovement: null };
    }

    // Try numeric comparison first
    const current = parseFloat(String(currentValue));
    const previous = parseFloat(String(previousValue));

    if (!isNaN(current) && !isNaN(previous)) {
      if (current === previous) return { trend: null, isImprovement: null };
      const trendDirection: 'up' | 'down' = current > previous ? 'up' : 'down';
      // For numeric values, we need thermal scale to determine if up/down is improvement
      const thermalConfig =
        this.schemaService.getThermalScaleConfig(symptomKey, currentMethod) ||
        this.schemaService.getThermalScaleConfig(symptomKey, previousMethod);

      if (thermalConfig && !!(thermalConfig as any).linear) {
        const currentSeverity = this.getColorValue(current, thermalConfig);
        const previousSeverity = this.getColorValue(previous, thermalConfig);
        if (currentSeverity != null && previousSeverity != null) {
          return {
            trend: trendDirection,
            isImprovement: currentSeverity < previousSeverity
          };
        }
      }
      // If no thermal config or severity couldn't be determined, still show trend
      // but mark improvement as null since we can't determine direction
      return { trend: trendDirection, isImprovement: null };
    }

    // For non-numeric values, use thermal scale severity comparison
    const thermalConfig =
      this.schemaService.getThermalScaleConfig(symptomKey, currentMethod) ||
      this.schemaService.getThermalScaleConfig(symptomKey, previousMethod);

    if (!thermalConfig) {
      return { trend: null, isImprovement: null };
    }

    // Normalize values to handle legacy formats
    const normalizedCurrentValue = this.normalizeValue(currentValue, thermalConfig);
    const normalizedPreviousValue = this.normalizeValue(previousValue, thermalConfig);

    const isLinear = !!(thermalConfig as any).linear;
    if (!isLinear &&
      (typeof normalizedCurrentValue === 'number' || typeof normalizedPreviousValue === 'number')) {
      return { trend: null, isImprovement: null };
    }

    const currentSeverity = this.getColorValue(normalizedCurrentValue, thermalConfig);
    const previousSeverity = this.getColorValue(normalizedPreviousValue, thermalConfig);

    if (
      currentSeverity == null ||
      previousSeverity == null ||
      currentSeverity === previousSeverity
    ) {
      return { trend: null, isImprovement: null };
    }

    const trendDirection: 'up' | 'down' = currentSeverity > previousSeverity ? 'up' : 'down';
    const result: { trend: 'up' | 'down'; isImprovement: boolean } = {
      trend: trendDirection,
      isImprovement: currentSeverity < previousSeverity
    };
    return result;
  }

  private normalizeValue(value: any, thermalConfig: any): any {
    if (value == null) return value;

    const valueStr = String(value).toLowerCase();

    // Check if value already exists in thermal config
    const configValues = Object.values(thermalConfig).map(v => String(v).toLowerCase());
    if (configValues.includes(valueStr)) {
      return value;
    }

    // Handle legacy "gradeX" format
    const gradeMatch = valueStr.match(/^grade(\d+)$/);
    if (gradeMatch) {
      const gradeNum = parseInt(gradeMatch[1], 10);
      const gradeToSeverity: { [key: number]: string } = {
        0: 'normal',
        1: 'mild',
        2: 'moderate',
        3: 'severe',
        4: 'severe'
      };

      const mappedValue = gradeToSeverity[gradeNum];
      if (mappedValue) {
        for (const [, configValue] of Object.entries(thermalConfig)) {
          if (String(configValue).toLowerCase() === mappedValue) {
            return configValue;
          }
        }
      }
    }

    return value;
  }

  private getColorValue(value: any, thermalConfig: any): number | null {
    if (!thermalConfig || value == null) return null;

    // Use ThermalScaleService logic to match the form view exactly
    // This returns a normalized value (0-1 range) for comparison

    // Handle linear scale (e.g., "0-100")
    if (thermalConfig.linear) {
      const numValue = parseFloat(String(value));
      if (!isNaN(numValue)) {
        // For linear scales, normalize the value to 0-1 range
        const normalized = this.normalizeLinearValue(numValue, thermalConfig.linear);
        return normalized;
      }
    }

    // Handle buckitized/discrete scale - use invert to reverse the map
    // This matches the ThermalScaleService implementation
    const valueStr = String(value);

    // Create inverse map: value -> color
    const inverseMap: { [key: string]: string } = {};
    for (const [color, configValue] of Object.entries(thermalConfig)) {
      if (configValue != null && color !== 'labels' && color !== 'linear') {
        inverseMap[String(configValue)] = color;
      }
    }

    // Find the color for this value
    const color = inverseMap[valueStr];

    if (color) {
      // Map color to normalized value (0-1 range) to match ThermalScaleService
      // This uses the discreteColorRangeMap from ThermalScaleService
      const normalizedValue = this.colorToNormalizedValue(color);
      return normalizedValue;
    }

    // Fallback: try labels if they exist
    if (thermalConfig.labels) {
      const valueStrLower = valueStr.toLowerCase();
      const labels = thermalConfig.labels;

      for (const [color, labelValue] of Object.entries(labels)) {
        if (String(labelValue).toLowerCase() === valueStrLower) {
          const normalizedValue = this.colorToNormalizedValue(color);
          return normalizedValue;
        }
      }
    }

    return null;
  }

  /**
   * Normalize a numeric value within a linear range (e.g., "0-100")
   * Returns a value between 0 and 1
   */
  private normalizeLinearValue(value: number, condition: string): number | null {
    const rangeRegex = /^([-+]?[0-9]*\.?[0-9]+)-([-+]?[0-9]*\.?[0-9]+)$/;
    if (rangeRegex.test(condition)) {
      const matches = condition.match(rangeRegex);
      const testValue1 = parseFloat(matches[1]);
      const testValue2 = parseFloat(matches[2]);
      const min = Math.min(testValue1, testValue2);
      const max = Math.max(testValue1, testValue2);

      if (min <= value && value <= max) {
        let normalized = (value - min) / (max - min);
        if (testValue1 > testValue2) {
          normalized = 1 - normalized;
        }
        return Number.parseFloat(normalized.toPrecision(6));
      }
    }
    return null;
  }

  /**
   * Map color to normalized value (0-1 range)
   * This matches the discreteColorRangeMap from ThermalScaleService
   */
  private colorToNormalizedValue(color: string): number | null {
    const colorKey = color.replace(/\s/g, '').toLowerCase();

    // Use the midpoint of each color range for comparison
    // This matches ThermalScaleService.discreteColorRangeMap
    const colorToValue: { [key: string]: number } = {
      green: 0.0,
      yellow: 0.2865, // midpoint of [0.24, 0.333]
      yelloworange: 0.4165, // midpoint of [0.333, 0.5]
      orangeyellow: 0.4165,
      orange: 0.5835, // midpoint of [0.5, 0.667]
      orangered: 0.75, // midpoint of [0.667, 0.833]
      redorange: 0.75,
      red: 1.0
    };

    return colorToValue[colorKey] !== undefined ? colorToValue[colorKey] : null;
  }
}
